import { useEffect, useState } from 'react';
import { Mail, Pencil, X, ToggleLeft, ToggleRight } from 'lucide-react';
import api, { EmailNotification } from '../../services/api';

export default function AdminNotifications() {
  const [notifications, setNotifications] = useState<EmailNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingNotification, setEditingNotification] = useState<EmailNotification | null>(null);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const data = await api.getEmailNotifications();
        setNotifications(data);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, []);

  const toggleNotification = async (id: string, enabled: boolean) => {
    try {
      await api.updateEmailNotification(id, { enabled: !enabled });
      setNotifications(notifications.map(n => n.id === id ? { ...n, enabled: !enabled } : n));
    } catch (error) {
      console.error('Failed to toggle notification:', error);
    }
  };

  const saveTemplate = async () => {
    if (!editingNotification) return;
    try {
      await api.updateEmailNotification(editingNotification.id, { template: editingNotification.template });
      setNotifications(notifications.map(n => n.id === editingNotification.id ? editingNotification : n));
      setEditingNotification(null);
    } catch (error) {
      console.error('Failed to save template:', error);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Email Notifications</h1>
        <p className="text-gray-500 mt-1">Configure automated email notifications.</p>
      </div>

      <div className="grid gap-4">
        {notifications.map((notification) => (
          <div key={notification.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-indigo-50 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{notification.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{notification.description}</p>
                </div>
              </div>
              <button onClick={() => toggleNotification(notification.id, notification.enabled)} className="flex items-center gap-2">
                {notification.enabled ? (
                  <ToggleRight className="w-10 h-6 text-indigo-600" />
                ) : (
                  <ToggleLeft className="w-10 h-6 text-gray-400" />
                )}
              </button>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Email Template</span>
                <button onClick={() => setEditingNotification(notification)} className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700">
                  <Pencil className="w-4 h-4" /> Edit Template
                </button>
              </div>
              <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-600 font-mono">
                {notification.template}
              </div>
            </div>
          </div>
        ))}
      </div>

      {editingNotification && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold">Edit Template</h2>
              <button onClick={() => setEditingNotification(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Template (use {'{{variable}}'} for dynamic content)
              </label>
              <textarea
                value={editingNotification.template}
                onChange={(e) => setEditingNotification({ ...editingNotification, template: e.target.value })}
                rows={6}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
              />
            </div>
            <div className="flex justify-end gap-3 p-6 border-t">
              <button onClick={() => setEditingNotification(null)} className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={saveTemplate} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Save Template</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
